import type { Plan } from "@helmr/checkin";
import { daysBetween } from "@helmr/checkin";
import { formatDate } from "@helmr/ui";

export interface ReasonContext {
  /** The person's cached plans, when the roster has them. Lets the screen say which date. */
  plans?: Plan[];
  /** Business-local `YYYY-MM-DD` at the branch. */
  onDate: string;
  locale: string;
}

const day = (ymd: string, locale: string) => formatDate(ymd, locale, "short");

/** Plain words for a reason code, as the API or `decideEntry` gives it. Unknown codes read generically, never as the raw code. */
export function reasonText(code: string, ctx: ReasonContext): string {
  const plans = ctx.plans ?? [];
  switch (code) {
    case "expired": {
      const ended = plans.map((p) => p.endDate).filter((d) => d < ctx.onDate).sort().pop();
      return ended ? `Plan expired on ${day(ended, ctx.locale)}` : "Plan expired";
    }
    case "not_started": {
      const next = plans.map((p) => p.startDate).filter((d) => d > ctx.onDate).sort()[0];
      return next ? `Plan starts on ${day(next, ctx.locale)}` : "Plan has not started yet";
    }
    case "no_active_plan": return "No active plan";
    case "frozen": return "Plan is frozen";
    case "wrong_branch": return "Wrong branch";
    case "visit_limit_hit": return "Visit limit reached at this branch";
    case "outside_hours": return "Outside opening hours";
    case "no_sessions_left": return "No sessions left on the pack";
    case "member_inactive": return "Membership is not active";
    case "expires_soon": {
      const end = plans.filter((p) => p.status === "active" && p.endDate >= ctx.onDate).map((p) => p.endDate).sort().pop();
      if (!end) return "Plan ends soon";
      const left = daysBetween(ctx.onDate, end);
      return left === 0 ? "Plan ends today" : left === 1 ? "Plan ends tomorrow" : `Plan ends in ${left} days`;
    }
    case "balance_due": return "Balance due. Please see the front desk";
    default: return "Entry not allowed";
  }
}

export type ResultTone = "ok" | "warn" | "deny";

export interface Verdict {
  tone: ResultTone;
  /** The big line: "Welcome", or the main reason in red. */
  headline: string;
  /** Smaller lines under it: warnings on amber, further reasons on red. */
  notes: string[];
}

/** Colour and reasons → what the result screen says. `green` and `amber` both welcome the member; red leads with the reason. */
export function verdictFor(result: "allow" | "deny", colour: "green" | "amber" | "red", reasons: readonly string[], ctx: ReasonContext): Verdict {
  const words = reasons.map((r) => reasonText(r, ctx));
  if (result === "deny") {
    const [first = "Entry not allowed", ...rest] = words;
    return { tone: "deny", headline: first, notes: rest };
  }
  return { tone: colour === "amber" ? "warn" : "ok", headline: "Welcome", notes: words };
}
